import {
  RoutineConfig,
  formatShortDateAndTimeOrSameDay,
  formatShortDateOrSameDay,
  useAssetIntegrationReadings,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import { IntegrationReadingRow } from '@gripp/shared-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';

type AssetIntegrationReadingsSectionProps = {
  assetId: string;
};

/**
 * Renders integration-sourced (GeoTab / John Deere) counter readings as
 * lightweight, non-navigating activity rows above the issue list on the asset
 * detail surfaces (Activity fold + full asset activity list).
 *
 * The rows are derived READ-TIME from existing `assetroutine` docs — no records
 * are written. They live in a separate rendering pass above the paginated issue
 * list, mirroring the `IntegrationDigestSection` / `CampaignRowsSection`
 * precedent in `activity/components/activityList.tsx`: the issue list is
 * paginated inside `ActivityListBase`, so a fully interleaved chronological
 * merge would require pulling the issue fetch up into this component. Readings
 * for a single asset are few and unpaginated, so a top section keeps the data
 * paths independent while staying visually consistent with the issue rows.
 *
 * Routine display name + uom are resolved from `workspaceAsset.routines` — data
 * already loaded for the asset page — so there is no N+1 lookup per reading.
 */
export const AssetIntegrationReadingsSection = ({
  assetId,
}: AssetIntegrationReadingsSectionProps) => {
  const { t } = useTranslation();
  const { readings } = useAssetIntegrationReadings(assetId);
  const { workspaceAsset } = useWorkspaceAsset(assetId, !assetId);

  // routineId -> config, for name + uom resolution without per-row queries.
  const configByRoutineId = useMemo(() => {
    const map = new Map<string, RoutineConfig>();
    workspaceAsset?.routines?.forEach((routine) => {
      if (routine?.id && routine.config) {
        map.set(routine.id, routine.config as RoutineConfig);
      }
    });
    return map;
  }, [workspaceAsset]);

  if (readings.length === 0) {
    return null;
  }

  const formatWhen =
    Platform.OS === 'web'
      ? formatShortDateAndTimeOrSameDay
      : formatShortDateOrSameDay;

  return (
    <View style={styles.section}>
      {readings.map((reading) => {
        // createdFrom is guaranteed an integration source by the query filter;
        // guard the schema-optional field anyway rather than assert non-null.
        const createdFrom = reading.createdFrom;
        if (!createdFrom) {
          return null;
        }

        const config = reading.routine?.id
          ? configByRoutineId.get(reading.routine.id)
          : undefined;
        const routineName =
          config?.title || t('integrations.readings.fallbackRoutineName');
        // uom from routine config first, falling back to the value carried on
        // the reading itself (the sync worker writes `data.uom`).
        const uom = config?.uom ?? reading.data?.uom;

        return (
          <IntegrationReadingRow
            key={reading.id}
            testID={`integration-reading-row-${reading.id}`}
            routineName={routineName}
            count={reading.data?.count}
            oldValue={reading.data?.oldValue}
            uom={uom}
            when={reading.createdAt ? formatWhen(reading.createdAt) : ''}
          />
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    // Layout-only wrapper; each row paints its own background + spacing.
  },
});
